import type { OnChangeValue, Option } from '@ksyos/design-system';
import type { JSX } from 'react';
import { Alert, Button, FormGroup, FormParagraph, Select, Text } from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import type { ProcessActorProfession } from '@common/interfaces';
import type { IServiceActor, ProcessActorUpdate } from '@common/interfaces/api/processesApi';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { getServiceActors, updateProcessActors } from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

export type EditOption = {
    alertText?: string;
    isEditable: boolean;
    profession: ProcessActorProfession;
};

type ServiceActorOption = {
    alertText?: string;
    initialOrganization: Option;
    isEditable: boolean;
    organizations: Option[];
    profession: ProcessActorProfession;
};

type CareProvidersTeamEditProps = {
    editOptions: EditOption[];
    initialProcessActors: IServiceActor[];
    onClose: () => void;
    onSubmit: () => Promise<void>;
    processId: string;
    title?: string;
};

export function CareProvidersTeamEditModal(props: Readonly<CareProvidersTeamEditProps>): JSX.Element {
    const { editOptions, processId, initialProcessActors, title } = props;
    const abortSignal = useAbortSignal();
    const [hasSubmittedNoUpdates, setHasSubmittedNoUpdates] = useState(false);
    const [processActorUpdates, setProcessActorUpdates] = useState<ProcessActorUpdate[]>([]);
    const [serviceActorOptions, setServiceActorOptions] = useState<ServiceActorOption[]>([]);

    useEffect(() => {
        loadServiceActors();
    }, [process]);

    return (
        <Modal
            actionButtons={[
                <Button
                    key="cancel"
                    onClick={props.onClose}
                    variant="outline"
                >
                    {getSelectedLanguage().common.cancelling}
                </Button>,
                <Button
                    key="submit"
                    onClick={handleSubmit}
                    variant="solid"
                >
                    {getSelectedLanguage().patient.editCareProvidersTeam}
                </Button>,
            ]}
            isOpen={true}
            onClose={props.onClose}
            size="large"
            title={title ?? getSelectedLanguage().patient.editCareProvidersTeam}
        >
            <Text
                as="h2"
                paddingBottom={4}
                size="md"
            >
                Wijzig de gewenste organisatie(s) door de nieuwe te selecteren.
            </Text>
            {serviceActorOptions.map(({ alertText, initialOrganization, isEditable, organizations, profession }) => {
                const processActorUpdate = processActorUpdates.find(
                    (processActorUpdateItem) => processActorUpdateItem.profession === profession
                );
                const value = organizations.find(
                    (organization) => organization.value === processActorUpdate?.organizationId
                );

                return (
                    <Fragment key={profession}>
                        <FormGroup label={getSelectedLanguage().processActorProfession[profession]}>
                            {isEditable ? (
                                <Select
                                    onChange={(newValue: OnChangeValue<Option, false>) =>
                                        handleChange(newValue, initialOrganization, profession)
                                    }
                                    options={organizations}
                                    value={value ?? initialOrganization}
                                />
                            ) : (
                                <FormParagraph>{initialOrganization.label}</FormParagraph>
                            )}
                        </FormGroup>
                        {value && value.value !== initialOrganization.value && alertText && (
                            <Alert
                                appearance="info"
                                hasIcon={true}
                            >
                                {alertText}
                            </Alert>
                        )}
                    </Fragment>
                );
            })}
            {hasSubmittedNoUpdates && (
                <Alert
                    appearance="warning"
                    hasIcon={true}
                >
                    Je hebt nog niets gewijzigd. Wijzig de gewenste organisatie(s) door de nieuwe te selecteren
                </Alert>
            )}
        </Modal>
    );

    async function loadServiceActors() {
        const editableOptions = editOptions.filter((editOption) => editOption.isEditable);

        const { items } = await getServiceActors(
            processId,
            {
                professions: editableOptions.map((editOption) => editOption.profession),
            },
            abortSignal
        );

        const nextServiceActorOptions: ServiceActorOption[] = [];
        for (const initialProcessActor of initialProcessActors) {
            const editOption = editOptions.find(
                (editOptionItem) => editOptionItem.profession === initialProcessActor.profession
            );

            if (editOption) {
                nextServiceActorOptions.push({
                    alertText: editOption.alertText,
                    initialOrganization: {
                        label: initialProcessActor.organization.name,
                        value: initialProcessActor.organization.id,
                    },
                    isEditable: editOption.isEditable,
                    organizations: getOrganizationOptions(items, editOption?.profession),
                    profession: initialProcessActor.profession,
                });
            }
        }
        setServiceActorOptions(nextServiceActorOptions);
    }

    function getOrganizationOptions(serviceActors: IServiceActor[], profession?: ProcessActorProfession): Option[] {
        return serviceActors
            .filter((serviceActor) => serviceActor.profession === profession)
            .map((serviceActor) => ({
                label: serviceActor.organization.name,
                value: serviceActor.organization.id,
            }));
    }

    function handleChange(
        newValue: OnChangeValue<Option, false>,
        initialActor: Option,
        profession: ProcessActorProfession
    ): void {
        if (!newValue) {
            return;
        }

        const processActorUpdateIndex = processActorUpdates.findIndex(
            (processActorUpdate) => processActorUpdate.profession === profession
        );
        // Do nothing when there is no process actor update and the initial actor is selected
        if (processActorUpdateIndex === -1 && initialActor.value === newValue.value) {
            return;
        }

        const nextProcessActorUpdates = [...processActorUpdates];
        const processActorUpdate = {
            organizationId: newValue.value,
            practitionerId: null,
            profession,
        };
        if (processActorUpdateIndex !== -1 && initialActor.value === newValue.value) {
            // Remove from the process actor updates when it is the initial organization
            nextProcessActorUpdates.splice(processActorUpdateIndex, 1);
        } else if (processActorUpdateIndex !== -1 && initialActor.value !== newValue.value) {
            // Replace the process actor update when there is already a process actor update
            nextProcessActorUpdates[processActorUpdateIndex] = processActorUpdate;
        } else if (processActorUpdateIndex === -1 && initialActor.value !== newValue.value) {
            // Add to the process actor updates when there is no process actor update yet
            nextProcessActorUpdates.push(processActorUpdate);
        }

        setProcessActorUpdates(nextProcessActorUpdates);
    }

    async function handleSubmit(): Promise<void> {
        if (processActorUpdates.length <= 0) {
            return setHasSubmittedNoUpdates(true);
        }

        await updateProcessActors(processId, { processActors: processActorUpdates }, abortSignal);
        await props.onSubmit();
    }
}
